import { useContext, useEffect, useState } from "react";

import { FormType } from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { IS_PREVIEW_QUERY_PARAM } from "../../../../common/constants/url";
import { FrontendFormContext } from "../../../../common/contexts/FrontendFormContext";
import { useIsIframe } from "../../../../common/hooks/useIsIframe";
import { useIsOnboarding } from "../../../../common/hooks/useIsOnboarding";

import { getObjectFromLocalStorage } from "@/common/helpers/localStorage";
import { useLoggedOrganization } from "@/common/hooks/useLoggedOrganization";

type StoredFormData = {
  formId: string;
  token: string;
};

interface ShouldDisplayFormOwnerBannerProps {
  currentOrganizationId: string | null | undefined;
}

export const useShouldDisplayFormOwnerBanner = ({ currentOrganizationId }: ShouldDisplayFormOwnerBannerProps) => {
  const { formType, formData } = useContext(FrontendFormContext);
  const { loggedUserId } = useLoggedOrganization();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const { isOnboarding } = useIsOnboarding();
  const isIframe = useIsIframe();
  const [storedData, setStoredData] = useState<StoredFormData>({ formId: "", token: "" });

  useEffect(() => {
    // Only access localStorage in the browser
    const storedData: StoredFormData | undefined = getObjectFromLocalStorage?.("peer-to-peer-form-data");
    if (storedData) {
      setStoredData(storedData);
    }
  }, [formData.id]);

  const isPreviewTemplateMode = query.previewTemplateMode === "true";
  const isPreview = searchParams.get(IS_PREVIEW_QUERY_PARAM) === "true" || false;

  const formOrg = formData.organization;
  const isOwner = formOrg && currentOrganizationId && formOrg?.id === currentOrganizationId;

  const paymentFormTypes = [FormType.DonationForm, FormType.Ticketing];
  const isPaymentForm = formType && paymentFormTypes.includes(formType);

  const isFormPageDonorView = formData.id;

  const hasStoredCreatorToken = storedData.formId === formData.id && !!storedData.token;
  const isLoggedInCampaignCreator = Boolean(loggedUserId && "userId" in formData && formData.userId === loggedUserId);
  const isPeerToPeerCreator = hasStoredCreatorToken || isLoggedInCampaignCreator;

  return {
    isPeerToPeerCreator,
    isLoggedInCampaignCreator,
    token: storedData.token,
    shouldDisplayFormOwnerBanner:
      (isOwner || isPeerToPeerCreator) &&
      !isOnboarding &&
      !isPreviewTemplateMode &&
      !isPreview &&
      !isIframe &&
      isPaymentForm &&
      isFormPageDonorView,
  };
};
